import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FileText, Download, Plus, Trash2, Save, Send, Search, CheckCircle, Clock, Hash, Loader2, Euro, Info } from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (d) => {
  if (!d) return '--';
  try { return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(d)); }
  catch { return d; }
};

const RecusFiscauxPage = () => {
  const [receipts, setReceipts] = useState([]);
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newReceipt, setNewReceipt] = useState({
    user_id: '', amount: '', payment_method: 'virement', transaction_id: '', notes: ''
  });

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  const canGenerate = me?.role === 'super_admin' || (me?.role === 'regional' && me?.can_generate_receipts);
  const canDelete = me?.role === 'super_admin';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [receiptsRes, usersRes, meRes] = await Promise.all([
        fetch(`${API_BASE}/api/fiscal-receipts`, { headers }),
        fetch(`${API_BASE}/api/users`, { headers }),
        fetch(`${API_BASE}/api/auth/me`, { headers }),
      ]);
      if (receiptsRes.ok) setReceipts(await receiptsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (meRes.ok) setMe(await meRes.json());
    } catch { toast.error('Erreur de chargement'); }
    setLoading(false);
  };

  const handleCreateReceipt = async () => {
    if (!newReceipt.user_id || !newReceipt.amount) {
      toast.error('Remplissez les champs obligatoires');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/fiscal-receipts`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newReceipt, amount: parseFloat(newReceipt.amount) })
      });
      if (res.ok) {
        toast.success('Reçu créé');
        setDialogOpen(false);
        setNewReceipt({ user_id: '', amount: '', payment_method: 'virement', transaction_id: '', notes: '' });
        fetchData();
      } else toast.error('Erreur lors de la création');
    } catch { toast.error('Erreur de connexion'); }
    setSaving(false);
  };

  const downloadPDF = async (receipt) => {
    try {
      const res = await fetch(`${API_BASE}/api/fiscal-receipts/${receipt.id}/pdf`, { headers });
      if (!res.ok) { toast.error('Erreur lors du téléchargement'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attestation_${receipt.receipt_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur de téléchargement'); }
  };

  const sendReceipt = async (receipt) => {
    try {
      const res = await fetch(`${API_BASE}/api/fiscal-receipts/${receipt.id}/send`, { method: 'POST', headers });
      if (res.ok) { toast.success('Reçu marqué comme envoyé'); fetchData(); }
      else toast.error('Erreur');
    } catch { toast.error('Erreur de connexion'); }
  };

  const deleteReceipt = async (id) => {
    if (!confirm('Supprimer ce reçu ?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/fiscal-receipts/${id}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Reçu supprimé'); fetchData(); }
      else toast.error('Erreur');
    } catch { toast.error('Erreur de connexion'); }
  };

  const filteredReceipts = receipts.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (r.beneficiary_name?.toLowerCase().includes(s) ||
        r.receipt_number?.toLowerCase().includes(s) ||
        r.beneficiary_email?.toLowerCase().includes(s));
    }
    return true;
  });

  const stats = {
    total: receipts.length,
    sent: receipts.filter(r => r.status === 'sent').length,
    draft: receipts.filter(r => r.status === 'draft').length,
    totalAmount: receipts.reduce((s, r) => s + (r.amount || 0), 0),
  };

  const PAYMENT_METHODS = [
    { value: 'virement', label: 'Virement' },
    { value: 'cb', label: 'Carte bancaire' },
    { value: 'cheque', label: 'Chèque' },
    { value: 'hello_asso', label: 'HelloAsso' },
    { value: 'especes', label: 'Espèces' },
  ];

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-gray-400" size={24} /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6" data-testid="recus-fiscaux-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shadow-sm">
              <FileText className="text-amber-600" size={18} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">Reçus Fiscaux</h1>
              <p className="text-xs text-gray-400 mt-0.5">Attestations conformes au modèle officiel SFOALC</p>
            </div>
          </div>
          <button onClick={() => setDialogOpen(true)} disabled={!canGenerate}
            title={!canGenerate ? "La génération de reçus n'est pas activée pour votre compte. Contactez l'administrateur SFOALC." : ""}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#1e3a5f] rounded-xl hover:bg-[#2a4a6f] transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed" data-testid="btn-new-receipt">
            <Plus size={15} /> Nouveau reçu
          </button>
        </div>

        {/* Read-only banner for regional admins without permission */}
        {me && !canGenerate && (
          <div className="flex items-start gap-3 p-4 bg-amber-50/80 border border-amber-200 rounded-2xl" data-testid="receipts-readonly-banner">
            <Info size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold">Mode consultation uniquement</p>
              <p className="text-amber-700 mt-0.5 text-xs leading-relaxed">
                Vous pouvez consulter et télécharger les reçus existants. La génération de nouveaux reçus fiscaux doit être activée par l'administrateur SFOALC pour votre compte.
              </p>
            </div>
          </div>
        )}

        {/* Info banner — official template */}
        <div className="flex items-start gap-3 p-4 bg-blue-50/60 border border-blue-100 rounded-2xl">
          <Info size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold">Modèle officiel SFOALC</p>
            <p className="text-blue-700 mt-0.5 text-xs leading-relaxed">
              Le PDF généré reproduit fidèlement l'attestation officielle SFOALC : en-tête, bureau national à gauche (dynamique depuis la collection Bureau), texte de certification avec le trésorier en exercice, et pied de page Secrétariat / Trésorerie. Les variables (nom du bénéficiaire, adresse, montant, mode de règlement, date) sont remplies automatiquement.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-lg flex items-center justify-center"><Hash size={14} /></div>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Total reçus</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center"><CheckCircle size={14} /></div>
            </div>
            <p className="text-2xl font-bold text-emerald-600">{stats.sent}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Envoyés</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-amber-100 text-amber-600 rounded-lg flex items-center justify-center"><Clock size={14} /></div>
            </div>
            <p className="text-2xl font-bold text-amber-600">{stats.draft}</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Brouillons</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center"><Euro size={14} /></div>
            </div>
            <p className="text-2xl font-bold text-blue-600">{stats.totalAmount.toFixed(0)} €</p>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Montant total</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 relative min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" />
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Rechercher par nom, email, n° de reçu..."
              className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none" data-testid="receipt-search" />
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2.5 text-sm border border-gray-200 rounded-xl appearance-none bg-white" data-testid="receipt-status-filter">
            <option value="all">Tous les statuts</option>
            <option value="draft">Brouillons</option>
            <option value="sent">Envoyés</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm" data-testid="receipts-table">
          {filteredReceipts.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="mx-auto text-gray-300 mb-3" size={40} />
              <p className="text-gray-500 font-medium">Aucun reçu trouvé</p>
              {canGenerate && <p className="text-sm text-gray-400 mt-1">Créez votre premier reçu fiscal</p>}
              {canGenerate && (
                <button onClick={() => setDialogOpen(true)} className="mt-4 px-4 py-2 bg-[#1e3a5f] text-white text-sm font-semibold rounded-xl hover:bg-[#2a4a6f] transition-colors">
                  <Plus size={14} className="inline mr-1" /> Nouveau reçu
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left text-[10px] uppercase tracking-wider font-bold text-gray-400 px-4 py-3">N° reçu</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-bold text-gray-400 px-4 py-3">Bénéficiaire</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-bold text-gray-400 px-4 py-3">Montant</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-bold text-gray-400 px-4 py-3">Statut</th>
                    <th className="text-left text-[10px] uppercase tracking-wider font-bold text-gray-400 px-4 py-3">Date</th>
                    <th className="text-right text-[10px] uppercase tracking-wider font-bold text-gray-400 px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReceipts.map((receipt) => (
                    <tr key={receipt.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors" data-testid={`receipt-row-${receipt.id}`}>
                      <td className="px-4 py-3.5">
                        <span className="text-sm font-mono font-bold text-[#1e3a5f]">{receipt.receipt_number}</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <p className="text-sm font-semibold text-gray-800">{receipt.beneficiary_name}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{receipt.beneficiary_email}</p>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="text-sm font-bold text-gray-900">{receipt.amount} €</span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                          receipt.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                          receipt.status === 'draft' ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${
                            receipt.status === 'sent' ? 'bg-emerald-500' :
                            receipt.status === 'draft' ? 'bg-amber-500' :
                            'bg-gray-400'
                          }`} />
                          {receipt.status === 'sent' ? 'Envoyé' : receipt.status === 'draft' ? 'Brouillon' : 'En attente'}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-400">{formatDate(receipt.issue_date)}</td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => downloadPDF(receipt)}
                            className="p-2 hover:bg-emerald-50 rounded-lg transition-colors" title="Télécharger PDF" data-testid={`download-receipt-${receipt.id}`}>
                            <Download size={15} className="text-emerald-500" />
                          </button>
                          {canGenerate && (
                            <button onClick={() => sendReceipt(receipt)} disabled={receipt.status === 'sent'}
                              className="p-2 hover:bg-violet-50 rounded-lg transition-colors disabled:opacity-30" title="Marquer envoyé" data-testid={`send-receipt-${receipt.id}`}>
                              <Send size={15} className="text-violet-500" />
                            </button>
                          )}
                          {canDelete && (
                            <button onClick={() => deleteReceipt(receipt.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors" title="Supprimer" data-testid={`delete-receipt-${receipt.id}`}>
                              <Trash2 size={15} className="text-red-400" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* New Receipt Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <Plus size={18} className="text-[#1e3a5f]" /> Nouveau reçu fiscal
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Bénéficiaire *</Label>
                <select value={newReceipt.user_id} onChange={e => setNewReceipt(p => ({ ...p, user_id: e.target.value }))}
                  className="w-full mt-1.5 px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-200 outline-none" data-testid="receipt-user-select">
                  <option value="">Sélectionner un membre...</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Montant (€) *</Label>
                  <Input type="number" step="0.01" value={newReceipt.amount} onChange={e => setNewReceipt(p => ({ ...p, amount: e.target.value }))}
                    placeholder="150.00" className="mt-1.5 rounded-xl" data-testid="receipt-amount" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Paiement</Label>
                  <select value={newReceipt.payment_method} onChange={e => setNewReceipt(p => ({ ...p, payment_method: e.target.value }))}
                    className="w-full mt-1.5 px-3 py-2.5 border border-gray-200 rounded-xl text-sm" data-testid="receipt-payment">
                    {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold">N° Transaction / Commande</Label>
                <Input type="text" value={newReceipt.transaction_id} onChange={e => setNewReceipt(p => ({ ...p, transaction_id: e.target.value }))}
                  placeholder="Optionnel — sera utilisé comme N° de commande dans le PDF" className="mt-1.5 rounded-xl" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Notes internes</Label>
                <textarea value={newReceipt.notes} onChange={e => setNewReceipt(p => ({ ...p, notes: e.target.value }))} rows={2}
                  className="w-full mt-1.5 px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-200 outline-none resize-none" placeholder="Non visibles dans l'attestation" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-xl">Annuler</Button>
                <Button onClick={handleCreateReceipt} disabled={saving} className="flex-1 bg-[#1e3a5f] hover:bg-[#162d4a] text-white rounded-xl" data-testid="submit-receipt">
                  {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : <Save size={16} className="mr-2" />}
                  Créer le reçu
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default RecusFiscauxPage;
